/* eslint-disable no-param-reassign */
// eslint-disable-next-line import/no-cycle
import { Analytics, fireUtagEvent, fireUtagView } from '@garmin/support-analytics';
// eslint-disable-next-line import/no-cycle
import Utils from '../../../../lib/utils';
import { triggerSearchQueryAnalytics } from '../../util';
import { isValidURLParam, updateUrlParametersNative, updatePageTitle } from './util';

const getDefaultState = () => ({
  urlParams: { // Keep in alphabetical order
    altLocale: false,
    altLocaleSearchPrompt: false,
    contactId: false,
    contactOverride: false,
    customerGuid: false,
    faq: false,
    feature: false,
    focus: false,
    identifier: false,
    interactionId: false,
    partNumber: false,
    prevSearchTerm: false,
    productID: false,
    productSearch: false,
    referrer: false,
    searchQuery: false,
    searchType: false,
    sessionId: false,
    tab: false,
    textPage: false,
    topicTag: false,
    utm_source: false,
    version: false, // the API version to use
    videoPage: false,
    topFaq: false,
  },
});

export default {
  state: getDefaultState(),

  getters: {
    isValidURLParam,
    urlParams: (state) => state.urlParams,
    getURLParam: (state) => (option) => (Object.hasOwn(state.urlParams, option) ? state.urlParams[option] : false),
    isSearchTypeNoProduct: (state) => state.urlParams.searchType === 'noProduct',
    apiParams: (state, getters) => {
      const params = { ...state.urlParams };

      if (getters.product?.tagName) {
        params.productTagName = getters.product.tagName;
      }

      return params;
    },
    hasUserSearched: (state, getters) => {
      const searchQuery = getters.getURLParam('searchQuery');
      const topicTag = getters.getURLParam('topicTag');

      if (
        (typeof searchQuery !== 'undefined' && searchQuery.length)
        || (typeof topicTag !== 'undefined' && topicTag.length)
      ) {
        return true;
      }
      return false;
    },
    getUrlWithoutParam: (state) => (paramToIgnore) => {
      const params = Utils.getObjectCopy(state.urlParams);

      if (paramToIgnore && typeof paramToIgnore !== 'undefined') {
        params[paramToIgnore] = false;
      }

      return `${Utils.getBaseUrl()}/${Utils.getLocale()}${Utils.formatUrlParametersObject(params)}`;
    },

  },
  mutations: {
    urlParamsUpdate(state, params) {
      // Sanitize customerGuid
      if (params.customerGuid) {
        params.customerGuid = params.customerGuid.replace(/[^a-zA-Z0-9-]/gi, '');
      }
      state.urlParams = params;
    },
  },

  actions: {
    updatePageTitle,
    updateUrlParametersNative,
    resetSearchUrlParams({ commit, getters }) {
      const { urlParams } = getters;

      commit('urlParamsUpdate', {
        ...urlParams,
        prevSearchTerm: urlParams.searchQuery,
        textPage: false,
        videoPage: false,
        searchQuery: false,
        topicTag: false,
        faq: false,
        altLocale: false,
        altLocaleSearchPrompt: false,
      });
    },
    resetUrlParams({ dispatch }) {
      dispatch('updateUrlParams', { params: getDefaultState().urlParams, pushHistoryEntryFlag: true });
    },
    updateUrlParams({
      dispatch, commit, getters, rootGetters,
    }, { params, pushHistoryEntryFlag, submitSearchQuery }) {
      let promiseArray = [];
      commit('urlParamsUpdate', params);

      if (pushHistoryEntryFlag) {
        dispatch('updateUrlParametersNative', params);
      }

      if (params.productSearch && getters.productList.length === 0) {
        promiseArray.push(dispatch('fetchProductListByLocale', params.version));
      }

      if (
        getters.canUserIdentifyProduct(params)
        && getters.urlParams.productID !== getters.product.productId
        && getters.urlParams.identifier !== getters.product.identifier
      ) {
        promiseArray.push(dispatch('fetchProductDetails', {
          params: false,
        }));
      } else if (!getters.canUserIdentifyProduct(params)) {
        commit('productUpdate', false);
      }

      if (params.faq && params.faq !== getters.faqDetails.contentID) {
        promiseArray.push(dispatch('fetchFaqDetails', params));
      } else if (!params.faq) {
        commit('prevFaqUpdate', false);
        commit('faqDetailsUpdate', false);
      }

      if (
        !getters.canUserIdentifyProduct(params)
        && ((
          getters.canUserSearch(params)
          && !params.faq
        )
          || (params.tab === 'welcome' && !params.faq))
        && (!params.textPage && !params.videoPage)
      ) {
        promiseArray.push(dispatch('results/fetchTextResults'));
        promiseArray.push(dispatch('results/fetchVideoResults'));
        if (getters.isInternal) {
          promiseArray.push(dispatch('results/fetchTextResultsPrivate'));
        }
      } else if (!params.textPage && !params.videoPage) {
        commit('results/textResultsUpdate', false);
        commit('results/textResultsPrivateUpdate', false);
        commit('results/videoResultsUpdate', false);
      }

      // eslint-disable-next-line consistent-return
      return Promise.all(promiseArray).then(() => {
        promiseArray = [];

        if (
          getters.canUserIdentifyProduct(params)
          && ((
            getters.canUserSearch(params)
            && !params.faq
          )
            || (params.tab === 'welcome' && !params.faq))
          && (!params.textPage && !params.videoPage)
        ) {
          promiseArray.push(dispatch('results/fetchTextResults'));
          promiseArray.push(dispatch('results/fetchVideoResults'));

          if (getters.isInternal) {
            promiseArray.push(dispatch('results/fetchTextResultsPrivate'));
          }

          return Promise.all(promiseArray)
            .then(() => {
              if (submitSearchQuery) {
                triggerSearchQueryAnalytics(
                  getters['results/videoResults'],
                  getters['results/textResults'],
                  rootGetters.isInternal ? getters['results/textResultsPrivate'] : null,
                  rootGetters.product,
                  rootGetters.noProductFlowFlag,
                  params.searchQuery,
                );
              }
              dispatch('updatePageTitle');
            })
            .catch((err) => {
              console.log('updateUrlParams store.js error ->', err);
            });
        }

        if (submitSearchQuery) {
          triggerSearchQueryAnalytics(
            getters['results/videoResults'],
            getters['results/textResults'],
            rootGetters.isInternal ? getters['results/textResultsPrivate'] : null,
            rootGetters.product,
            rootGetters.noProductFlowFlag,
            params.searchQuery,
          );
        }

        dispatch('updatePageTitle');
      }).catch((err) => {
        console.log('updateUrlParams store.js error ->', err);
      });
    },

    async setParamsViaURL({ commit, getters, dispatch }) {
      let urlParams = window.location.search;
      const params = { ...getters.urlParams };
      let removeParamFlag = false;
      let noProductEnabled = false;

      // Remove utm_source parameter
      if (urlParams.indexOf('utm_source=') !== -1) {
        urlParams = urlParams.replace(/utm_source=\w+/, '');
        removeParamFlag = true;
      }

      // Remove sas_source parameter
      if (urlParams.indexOf('sas_source=') !== -1) {
        const sasSourcePattern = /sas_source=\w+/;
        const sasSourceParam = sasSourcePattern.exec(urlParams)[0];
        const sasSourceParamArr = sasSourceParam.split('=');

        urlParams = urlParams.replace(/sas_source=\w+/, '');
        removeParamFlag = true;

        fireUtagEvent({
          event_category: 'PS SAS',
          event_action: 'Redirect',
          event_label: sasSourceParamArr[1],
          ps_sas_source: sasSourceParamArr[1],
        });
      }

      if (urlParams.indexOf('login=true') !== -1) {
        Analytics.trackAnalyticsEvent({
          manualId: '_authentication-successful',
        });

        // eslint-disable-next-line no-useless-escape
        urlParams = urlParams.replace(/((\?|\&)login=true)+/, '');
        removeParamFlag = true;
      }

      if (urlParams.indexOf('searchType=noProduct') !== -1) {
        // eslint-disable-next-line no-useless-escape
        urlParams = urlParams.replace(/((\?|\&)searchType=noProduct)+/, '');
        removeParamFlag = true;
        noProductEnabled = true;
      }

      if (
        typeof urlParams !== 'undefined'
        && (urlParams.length > 0 || noProductEnabled)
      ) {
        urlParams = urlParams.replace(/^\?/, '');
        const urlParamsArr = urlParams.split('&').sort();

        urlParamsArr.forEach((param) => {
          const paramArr = param.split('=');

          if (paramArr.length === 2 && paramArr[0] === 'contactOverride') {
            if (paramArr[1] === true || paramArr[1] === 'true') {
              params.contactOverride = true;
            } else {
              params.contactOverride = false;
            }
          } else if (paramArr.length === 2 && getters.isValidURLParam(paramArr[0])) {
            params[decodeURI(paramArr[0])] = decodeURI(paramArr[1]);
          }
        });

        if (
          (
            params.topicTag
            || params.searchQuery
            || params.faq
            || noProductEnabled
          )
          && (!params.productID && !params.identifier && !params.partNumber)
        ) {
          commit('noProductFlowFlagUpdate', true);
          if (getters.topics.length === 0) {
            dispatch('fetchCommonTopics', {
              assignToTopics: true,
            });
          }
        }

        if (params.focus) {
          commit('isQuickLinkClickedFlagUpdate', true);
        }

        await dispatch('updateUrlParams', {
          params,
          pushHistoryEntryFlag: removeParamFlag,
        });
      } else if (removeParamFlag) {
        window.history.replaceState(null, null, `${window.location.origin}${window.location.pathname}`);
      }

      fireUtagView();
    },
  },

};
